import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createPricingTier, listPricingTiers, updatePricingTier, type PricingTier } from "@/integrations/backend/pricing";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogScrollContent, DialogTitle } from "@/components/ui/dialog";
import { CATALOG_STALE_BASE_MS, staleTimeWithJitterFromKey } from "@/lib/query-cache";
import { queryKeys } from "@/lib/query-keys";

type Tier = PricingTier;

const pricingQueryKey = queryKeys.pricingTiers.all;

export default function PricingTiersView() {
  const queryClient = useQueryClient();
  const tiersQuery = useQuery({
    queryKey: pricingQueryKey,
    queryFn: listPricingTiers,
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...pricingQueryKey]),
  });
  const tiers = tiersQuery.data ?? [];

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Tier | null>(null);
  const [form, setForm] = useState<Tier | null>(null);

  const openCreate = () => {
    setEditing(null);
    setForm({ id: "", name: "", blurb: "", features: [], price_monthly: 0, price_annual: 0, stripe_price_id_monthly: "", stripe_price_id_annual: "", highlight: false, sort_order: (tiers.length + 1) } as Tier);
    setOpen(true);
  };
  const openEdit = (t: Tier) => {
    setEditing(t);
    setForm({ ...t });
    setOpen(true);
  };

  const save = async () => {
    if (!form) return;
    const payload = { ...form, features: form.features } as any;
    if (editing) {
      await updatePricingTier(editing.id, payload);
    } else {
      delete payload.id;
      await createPricingTier(payload);
    }
    setOpen(false);
    setEditing(null);
    setForm(null);
    await queryClient.invalidateQueries({ queryKey: pricingQueryKey });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Pricing Tiers</h1>
          <p className="text-gray-600">Manage subscription tiers shown on the Pricing page.</p>
        </div>
        <Button onClick={openCreate}>Add Tier</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Tiers</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Monthly</TableHead>
                  <TableHead>Annual</TableHead>
                  <TableHead>Highlight</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tiers.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium">{t.name}</TableCell>
                    <TableCell>${t.price_monthly}</TableCell>
                    <TableCell>${t.price_annual}</TableCell>
                    <TableCell>{t.highlight ? "Yes" : "No"}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" onClick={() => openEdit(t)}>
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogScrollContent className="sm:max-w-[800px] bg-white text-black">
          <DialogHeader className="shrink-0">
            <DialogTitle>{editing ? "Edit Tier" : "Add Tier"}</DialogTitle>
          </DialogHeader>
          {form && (
            <DialogBody className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-700">Name</label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form!, name: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm text-gray-700">Sort Order</label>
                  <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form!, sort_order: Number(e.target.value) })} />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-700">Short Description</label>
                <Textarea value={form.blurb} onChange={(e) => setForm({ ...form!, blurb: e.target.value })} />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-700">Monthly Price</label>
                  <Input type="number" value={form.price_monthly} onChange={(e) => setForm({ ...form!, price_monthly: Number(e.target.value) })} />
                </div>
                <div>
                  <label className="text-sm text-gray-700">Annual Price</label>
                  <Input type="number" value={form.price_annual} onChange={(e) => setForm({ ...form!, price_annual: Number(e.target.value) })} />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-700">Stripe Price ID (Monthly)</label>
                  <Input value={form.stripe_price_id_monthly || ""} onChange={(e) => setForm({ ...form!, stripe_price_id_monthly: e.target.value || null })} placeholder="price_xxxxx" />
                </div>
                <div>
                  <label className="text-sm text-gray-700">Stripe Price ID (Annual)</label>
                  <Input value={form.stripe_price_id_annual || ""} onChange={(e) => setForm({ ...form!, stripe_price_id_annual: e.target.value || null })} placeholder="price_xxxxx" />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-700">Bullet Points (comma separated)</label>
                <Input
                  value={form.features.join(", ")}
                  onChange={(e) => setForm({ ...form!, features: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                />
              </div>
              <div>
                <label className="text-sm text-gray-700">Highlight (Most Popular)</label>
                <Input type="checkbox" checked={form.highlight} onChange={(e) => setForm({ ...form!, highlight: e.target.checked })} className="h-4 w-4" />
              </div>
            </DialogBody>
          )}
          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => setOpen(false)} className="text-black border-gray-300 hover:bg-gray-100">
              Cancel
            </Button>
            <Button onClick={save}>Save</Button>
          </DialogFooter>
        </DialogScrollContent>
      </Dialog>
    </div>
  );
}
